<template>
  <div class="sign-in">
    <AlertBar
      v-model="message.show"
      :type="message.type"
      :message="message.text"
      :detail="message.detail"
    >
    </AlertBar>
    <Busy :value="busy"></Busy> 
    <v-snackbar
      v-model="tempMessage.show"
      :color="tempMessage.type"
      :timeout="3000"
      top
    >
    {{tempMessage.text}}
    </v-snackbar>
    <v-container 
      fluid 
      class="sign-in__content"
      :class="contentModifierClass"
    >
      <v-row fill-height class="px-4 justify-center align-center text-center">
        <template v-if="!registrationComplete">
          <v-col cols="12" class="text-center mb-4 pb-4 text-h4 font-weight-black primary--text text-uppercase">
            Asset Risk Analyzer
          </v-col>
          <v-col cols="12" md="6">
            <div class="text-center text-h4 font-weight-bold pb-4 mx-auto" :style="$vuetify.breakpoint.mdAndUp ? 'width:65%' : 'width:90%'">            
              A FREE solution to analyze failure risk and benchmark asset performance            
            </div>
            <div class="text-center pb-4">
              <v-img class="text-center mx-auto" :width="$vuetify.breakpoint.mdAndUp ? '50%' : '90%'" src="/screens.png"></v-img>
            </div>   
            <div class="text-center">
              <v-btn                          
                color="primary"            
                class="white--text"
                href="https://hexagon.com/products/hxgn-apm-asset-risk-analyzer"
                target="_blank"                
              >
                Learn More
              </v-btn>
            </div>       
          </v-col>
          <v-col cols="12" md="6" class="text-center">
            <v-card elevation="0">
              <v-card-text>
                <LoginForm
                  :email="email"
                  :password="password"
                  :message="loginMessage"
                  @login="login"
                  @email-changed="email = $event"
                  @password-changed="password = $event"
                  @show-alert="loginMessage.show = $event"
                  @create-account="createAccount"
                >
                </LoginForm>                  
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" v-for="item in marketingItems" :key="item.id">
            <MarketingSection                      
              :image="item.image"
              :imageFirst="item.imageFirst"
              :title="item.title"
              :content="item.content"
            >
            </MarketingSection>
          </v-col>
        </template>
        <v-col v-else cols="12" class="text-center align-center justify-center">          
          <div class="pa-4 ma-4 text-center align-center justify-center">
            <v-icon size="64" color="success" left class="pr-1">fa-solid fa-circle-check</v-icon>
            <span class="text-h4 font-weight-black">Success!</span>
          </div>
          <div class="text-center text-h5 pa-4 ma-4">
              Check your email for an activation link.<br/><br/>After clicking the activation link you'll be able to set your password and sign-in to the Asset Risk Analyzer.
          </div>
          <div class="text-center text-h5 pa-4 ma-4">
              If you did not receive the email message, check your spam folders, if you still don't find it you can click <FakeLink @click="resendEmail">here</FakeLink> to resend the message.
          </div>
        </v-col>
      </v-row>
    </v-container>
    <RegistrationDialog ref="registrationDialog"></RegistrationDialog>
  </div>
</template>

<script>
import axios from 'axios'
import { AlertBar, Busy, ExceptionMessage } from '@itus/ui.common'
import { mapState } from 'vuex'
import Version from '../../public/version.json'
import LoginForm from '@/components/LoginForm'
import MarketingSection from '@/components/MarketingSection'
import RegistrationDialog from '@/components/registration/RegistrationDialog'
import FakeLink from '@/components/FakeLink'
import { buildApiUrl } from '@/components/api-url-builder'

export default {
  name: 'Signin',
  data() {
    return {
      email: '',
      password: '',
      busy: false,
      message: {
        show: false,
        type: 'error',
        text: ''
      },   
      loginMessage: {
        show: false,
        type: 'error',
        text: ''
      },   
      tempMessage: {
        show: false,
        type: 'success',
        text: ''
      },   
      loadedVersion: Version.version,
      marketingItems: [
        {
          id: 'riskiest',
          image: '/riskiest-assets.png',
          imageFirst: false,
          title: 'Identify your riskiest assets',
          content: `Determine which assets present the most risk to your plan and quantify the opportunity for improvement.
          <ul class='text-left'>
            <li>Identify the riskiest assets in your plan</li>
            <li>Know which assets are performing below industry benchmarks</li>
            <li>Quantify the impact of unreliability</li>
            <li>Expose blind spots in your asset management system</li>
          </ul>`
        },
        {
          id: 'benchmark',
          image: '/benchmark.png',
          imageFirst: true,
          title: 'Benchmark your performance',
          content: 'Analyze your asset performance against industry metrics to identify improvement targets.  Focus your optimization efforts on the assets not meeting reliability targets.'
        },
        {
          id: 'risk',
          image: '/risk-results.png',
          imageFirst: false,
          title: 'Identify riskiest assets to your plan',
          content: 'Set your failure risk threshold and see exactly what will prevent you from making the plan.  Evaluate the next production run or an annual budgeting cycle and drive improvements where it counts most.'
        },
        {
          id: 'opportunity',
          image: '/whatif-risk.png',
          imageFirst: true,
          title: 'Quantify impact and opportunity',
          content: 'Analyze the financial impacts due to equipment unreliability and determine opportunity for improvement.  Run what if scenarios to quantify the potential savings you will gain from reliability improvement efforts.'
        },
        {
          id: 'twins',
          image: '/asset-twin.png',
          imageFirst: false,
          title: 'Implement Asset Strategies to improve performance',
          content: 'Capture the savings opportunity by implementing strategies on the assets which are high risk and performing poorly.  Drive Asset Strategies to mitigate potential failure risks and ensure optimized maintenance activities are in place.'
        }
      ],
      registrationComplete: false
    }
  },
  components: {
    Busy,
    LoginForm,
    MarketingSection,
    RegistrationDialog,
    FakeLink,
    AlertBar
  },
  computed: {
    ...mapState({
      version: state => state.version,
    }),
    contentModifierClass() {
      if (this.message && this.message.show){
        return 'sign-in__content--with-alert'
      }
      return null
    }
  },
  methods: {
    showException(err, defaultMsg) {
      this.message = ExceptionMessage.generateMessage(err, defaultMsg)
    },
    async login() {
      this.message.show = false
      this.loginMessage.show = false
      if(!this.email || !this.password) {
        this.loginMessage = ExceptionMessage.generateMessage(null, "Email and password must be provided for login.")
        return
      }

      try {
        this.busy = true
        await this.$store.dispatch('login', { email: this.email, password: this.password })      
        this.$gtag.event('login')
        await this.$router.push(this.$store.getters.routeAfterLogin ? this.$store.getters.routeAfterLogin : '/home')
        await this.reloadPageIfNewerVersion()      
        this.$store.dispatch('routeAfterLogin', null)
      } catch(err) {
        this.loginMessage = ExceptionMessage.generateMessage(err, "Incorrect username or password.")
      } finally {
        this.busy = false
      }
    },
    async reloadPageIfNewerVersion() {
      await this.$store.dispatch('checkCurrentVersion')
      if(this.version !== this.loadedVersion)
        window.location.assign(window.location)
    },
    async createAccount() {
      let registrationId = await this.$refs.registrationDialog.show()
      if(registrationId) {
        this.registrationId = registrationId
        this.registrationComplete = true        
      } else {
        this.registrationComplete = false
      }
    },
    async resendEmail() {
      this.message.show = false
      this.busy = true
      try {
        await axios.post(buildApiUrl('/api/register'), {
          step: 'resend',
          id: this.registrationId
        })
        this.tempMessage.text = 'Activation email sent.'
        this.tempMessage.type = 'success'
        this.tempMessage.show = true
      } catch(err) {
        this.showException(err, 'Error sending activation email.')
      } finally {
        this.busy = false
      }
    },

  },
  mounted() {
    this.registrationComplete = false
    if(this.$route.query?.hs)
      this.$gtag.event('hs',  { 'hs_id': this.$route.query?.hs })
  }
}
</script>
<style lang="scss" scoped>
$app-bar-height: 48px;
$gradient-height: 4px;
$page-alert-bar-height: 40px;
 
.sign-in__content {
  height: calc(100vh - #{$app-bar-height} - #{$gradient-height});
  overflow: auto;

  &.sign-in__content--with-alert {
      height: calc(100vh - #{$app-bar-height} - #{$gradient-height} - #{$page-alert-bar-height});
    }
}
</style>